import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Download } from "lucide-react";
import plants from "@/data/plants.json";
import { PageHero } from "@/components/PageHero";
import { ProductCard } from "@/components/ProductCard";
import { DOCS } from "@/lib/site";
import { useLang } from "@/context/LangContext";

const CAT_EN = { Semua: "All", "Koleksi Sedang & Kecil": "Medium & Small Collection", "Bunga Hias": "Flowering Plants" };

const ALL = "Semua";
const CATS = [ALL, ...Array.from(new Set(plants.map((p) => p.category)))];
const num = (s) => parseInt(String(s).replace(/\D/g, ""), 10) || 0;

const Filters = ({ cat, setCat, q, setQ, sort, setSort }) => {
  const { t } = useLang();
  return (
  <div className="sticky top-16 sm:top-[72px] z-30 border-y border-leaf/10 bg-paper/90 backdrop-blur-xl">
    <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-5 py-3 sm:px-8 lg:flex-row lg:items-center">
      <div className="-mx-5 flex gap-2 overflow-x-auto px-5 lg:mx-0 lg:flex-1 lg:flex-wrap lg:px-0" data-lenis-prevent>
        {CATS.map((c) => (
          <button key={c} data-testid={`category-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setCat(c)}
            className={`shrink-0 rounded-full px-4 py-2 font-display text-xs font-bold transition-colors ${cat === c ? "bg-[var(--leaf)] text-white" : "bg-white text-[var(--ink)] ring-1 ring-leaf/15 hover:ring-[var(--leaf)]"}`}>
            {t(c, CAT_EN[c] || c)}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <label className="relative flex-1 lg:w-64">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-ink)]" />
          <input data-testid="plants-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Cari tanaman…", "Search plants…")}
            className="w-full rounded-full border border-leaf/20 bg-white py-2 pl-10 pr-4 text-sm outline-none focus:border-[var(--leaf)]" />
        </label>
        <select data-testid="plants-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-leaf/20 bg-white px-3 text-sm outline-none">
          <option value="default">{t("Urutan katalog", "Catalogue order")}</option>
          <option value="asc">{t("Harga terendah", "Lowest price")}</option>
          <option value="desc">{t("Harga tertinggi", "Highest price")}</option>
        </select>
      </div>
    </div>
  </div>
  );
};

export default function Plants() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("kategori");
  const [cat, setCatState] = useState(CATS.includes(initial) ? initial : ALL);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("default");
  const { t } = useLang();
  const setCat = (c) => { setCatState(c); setParams(c === ALL ? {} : { kategori: c }, { replace: true }); };

  const list = useMemo(() => {
    let l = plants.filter((p) => (cat === ALL || p.category === cat) && p.name.toLowerCase().includes(q.toLowerCase()));
    if (sort !== "default") l = [...l].sort((a, b) => (sort === "asc" ? 1 : -1) * ((num(a.price) || 1e9 * (sort === "asc" ? 1 : 0)) - (num(b.price) || 1e9 * (sort === "asc" ? 1 : 0))));
    return l;
  }, [cat, q, sort]);

  return (
    <>
      <PageHero testid="plants-hero" eyebrow="Plants Catalog · 2025" lines={["Potted", "Ornamental", "Plants."]} img="/assets/img/cat-window.jpg" imgAlt={t("Tanaman hias Minaqu", "Minaqu ornamental plants")}
        intro={t("137 tanaman hias tropis dan bunga hias dari kebun kami di Bogor. Jenis tanaman tambahan yang tidak tercantum tersedia berdasarkan permintaan.", "137 tropical ornamental and flowering plants from our nursery in Bogor. Additional varieties not listed are available upon request.")}>
        <a href={DOCS.plants} target="_blank" rel="noreferrer" data-testid="plants-download-pdf" className="mt-8 inline-flex items-center gap-2 font-display text-sm font-bold text-[var(--leaf)] underline-offset-4 hover:underline">
          <Download className="h-4 w-4" /> {t("Unduh katalog PDF asli", "Download the original PDF catalogue")}
        </a>
      </PageHero>
      <Filters {...{ cat, setCat, q, setQ, sort, setSort }} />
      <section className="mx-auto max-w-[1400px] px-5 py-12 sm:px-8 sm:py-16">
        <p data-testid="plants-result-count" className="mb-8 font-display text-sm font-bold text-[var(--muted-ink)]">{list.length} {t("tanaman", "plants")} · {t(cat, CAT_EN[cat] || cat)}</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-10 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
          {list.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
        {list.length === 0 && <p data-testid="plants-empty" className="py-20 text-center text-[var(--muted-ink)]">{t("Tanaman tidak ditemukan. Tanyakan via WhatsApp — jenis lain tersedia berdasarkan permintaan.", "No plants found. Ask us on WhatsApp — other varieties are available upon request.")}</p>}
        <p className="mt-16 text-xs text-[var(--muted-ink)]">(*) Additional plant varieties not featured in this catalogue are available upon request.</p>
      </section>
    </>
  );
}
